import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Gift } from "lucide-react";
import { Button } from "@/components/ui/button";
import { QtyControl } from "@/components/site/CartDrawer";
import { useCart } from "@/lib/cart";
import { imgUrl, inr } from "@/lib/api";

export const ProductCard = ({ product, categoryName }) => {
  const [sku, setSku] = useState(product.variants.find((v) => v.in_stock)?.sku || product.variants[0]?.sku);
  const [qty, setQty] = useState(1);
  const { add, setOpen } = useCart();
  const navigate = useNavigate();
  const v = product.variants.find((x) => x.sku === sku);
  if (!v) return null;
  const canBuy = v.in_stock && qty <= v.stock;

  const addToCart = (buyNow) => {
    add(product.id, v.sku, qty);
    if (buyNow) navigate("/checkout");
    else {
      toast.success(`${product.name} added to cart`);
      setOpen(true);
    }
  };

  return (
    <article className="fade-up flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white transition-shadow duration-300 hover:shadow-xl" data-testid={`product-card-${product.id}`}>
      <div className="relative aspect-square overflow-hidden bg-slate-100">
        <img src={imgUrl(product.image)} alt={product.name} className="h-full w-full object-cover transition-transform duration-500 hover:scale-105" loading="lazy" />
        {product.offer && (
          <span className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-1.5 bg-red-600 px-3 py-2 text-center text-xs font-bold uppercase tracking-wide text-white" data-testid="product-offer-ribbon">
            <Gift className="h-4 w-4 shrink-0" /> {product.offer}
          </span>
        )}
        {v.discount_pct > 0 && <span className="absolute left-3 top-3 rounded-full bg-amber-600 px-2.5 py-1 text-xs font-bold text-white" data-testid="product-discount">{v.discount_pct}% OFF</span>}
      </div>
      <div className="flex flex-1 flex-col gap-3 p-5">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-teal-700">{categoryName}</p>
          <h3 className="mt-1 font-display text-lg font-bold leading-tight" data-testid="product-name">{product.name}</h3>
          <p className="mt-1.5 line-clamp-2 text-sm text-slate-600">{product.description}</p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {product.variants.map((x) => (
            <button key={x.sku} onClick={() => { setSku(x.sku); setQty(1); }} className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${x.sku === sku ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 hover:border-slate-400"} ${!x.in_stock ? "opacity-50" : ""}`} data-testid={`product-size-${x.sku}`}>
              {x.size}
            </button>
          ))}
        </div>
        <div className="flex items-end justify-between">
          <div>
            <span className="font-mono-num text-xl font-bold" data-testid="product-price">{inr(v.price)}</span>
            {v.discount > 0 && <span className="font-mono-num ml-2 text-sm text-slate-400 line-through" data-testid="product-mrp">MRP {inr(v.mrp)}</span>}
            {!v.discount && v.mrp && <span className="ml-2 text-xs text-slate-500">MRP {inr(v.mrp)}</span>}
          </div>
          <span className="text-[11px] text-slate-500" data-testid="product-sku">SKU {v.sku}</span>
        </div>
        <p className={`text-xs font-semibold ${v.in_stock ? (v.stock <= 10 ? "text-amber-700" : "text-emerald-700") : "text-red-600"}`} data-testid="product-stock">
          {v.in_stock ? (v.stock <= 10 ? `Only ${v.stock} left` : `In stock (${v.stock})`) : "Out of stock"}
        </p>
        <div className="mt-auto flex items-center gap-2 pt-1">
          <QtyControl value={qty} onChange={setQty} max={Math.max(1, Math.min(20, v.stock))} testid={`product-qty-${product.id}`} />
          <Button variant="outline" className="h-9 flex-1 rounded-full" disabled={!canBuy} onClick={() => addToCart(false)} data-testid={`add-to-cart-${product.id}`}>Add to Cart</Button>
        </div>
        <Button className="h-10 rounded-full bg-amber-600 hover:bg-amber-700" disabled={!canBuy} onClick={() => addToCart(true)} data-testid={`buy-now-${product.id}`}>Buy Now</Button>
      </div>
    </article>
  );
};
